import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { injectQuery, skipToken } from '@tanstack/angular-query-experimental';
import { OAuthService } from 'angular-oauth2-oidc';
import * as jose from 'jose';
import { lastValueFrom } from 'rxjs';

import { environment } from '#environments/environment';
import parseDistinguishedName from '#utils/parseDistinguishedName';

export const supportedBuCodes = [
  'SFAC',
  'SIAC',
  'EHBG',
  'EHSK',
  'HHUN',
  'CSOB',
  'EHRO',
  'HERM',
  'TI',
  'EHNO',
  'EHFI',
  'EHDK',
  'EHSE',
  'HSWI',
  'CBAC',
  'CBNL',
  'EHIE',
  'HPOL',
  'EHBR',
  'EHCA',
  'ACI',
  'EBSG',
  'EHJP',
  'EHAU',
  'EHNZ',
  'EBHK',
  'EHTW',
  'EHGR',
  'EHMA',
  'EHZA',
  'EBCN',
  'CSEC',
  'EHTR',
  'EHAE',
] as const;

export type SupportedBuCode = (typeof supportedBuCodes)[number];

const userBuToCountryAndBuCodeMap: Record<string, { countryCode: string; buCode: SupportedBuCode }> = {
  GERMANY: {
    countryCode: 'DE',
    buCode: 'HERM',
  },
  FRANCE: {
    countryCode: 'FR',
    buCode: 'SFAC',
  },
  ITALY: {
    countryCode: 'IT',
    buCode: 'SIAC',
  },
  HUNGARY: {
    countryCode: 'HU',
    buCode: 'HHUN',
  },
  CZECH: {
    countryCode: 'CZ',
    buCode: 'CSOB',
  },
  ROMANIA: {
    countryCode: 'RO',
    buCode: 'EHRO',
  },
  SLOWAKIA: {
    countryCode: 'SK',
    buCode: 'EHSK',
  },
  BULGARIA: {
    countryCode: 'BG',
    buCode: 'EHBG',
  },
  UK: {
    countryCode: 'GB',
    buCode: 'TI',
  },
  NORWAY: {
    countryCode: 'NO',
    buCode: 'EHNO',
  },
  FINLAND: {
    countryCode: 'FI',
    buCode: 'EHFI',
  },
  DENMARK: {
    countryCode: 'DK',
    buCode: 'EHDK',
  },
  SWEDEN: {
    countryCode: 'SE',
    buCode: 'EHSE',
  },
  SWITZERLAND: {
    countryCode: 'CH',
    buCode: 'HSWI',
  },
  BELGIUM: {
    countryCode: 'BE',
    buCode: 'CBAC',
  },
  NETHERLANDS: {
    countryCode: 'NL',
    buCode: 'CBNL',
  },
  IRELAND: {
    countryCode: 'IE',
    buCode: 'EHIE',
  },
  POLAND: {
    countryCode: 'PL',
    buCode: 'HPOL',
  },
  BRASIL: {
    countryCode: 'BR',
    buCode: 'EHBR',
  },
  CANADA: {
    countryCode: 'CA',
    buCode: 'EHCA',
  },
  USA: {
    countryCode: 'US',
    buCode: 'ACI',
  },
  SINGAPORE: {
    countryCode: 'SG',
    buCode: 'EBSG',
  },
  JAPAN: {
    countryCode: 'JP',
    buCode: 'EHJP',
  },
  AUSTRALIA: {
    countryCode: 'AU',
    buCode: 'EHAU',
  },
  'NEW ZEALAND': {
    countryCode: 'NZ',
    buCode: 'EHNZ',
  },
  CHINA: {
    countryCode: 'CN',
    buCode: 'EBHK',
  },
  TAIWAN: {
    countryCode: 'TW',
    buCode: 'EHTW',
  },
  GREECE: {
    countryCode: 'GR',
    buCode: 'EHGR',
  },
  MOROCCO: {
    countryCode: 'MA',
    buCode: 'EHMA',
  },
  'SOUTH AFRICA': {
    countryCode: 'ZA',
    buCode: 'EHZA',
  },
  'CHINA JV': {
    countryCode: 'CN',
    buCode: 'EBCN',
  },
  PORTUGAL: {
    countryCode: 'PT',
    buCode: 'CSEC',
  },
  TURKEY: {
    countryCode: 'TR',
    buCode: 'EHTR',
  },
  UAE: {
    countryCode: 'AE',
    buCode: 'EHAE',
  },
};

const specialBusinessUnitToCountries: Record<string, string[]> = {
  NORDIC: ['NORWAY', 'FINLAND', 'DENMARK', 'SWEDEN'],
  'CPIC-EH': ['CHINA JV'],
};

function getDataFromBusinessUnit(businessUnit: string | undefined, data: 'countryCode' | 'buCode'): string[] {
  if (!businessUnit) return [];
  const correspondingCountries = specialBusinessUnitToCountries[businessUnit];
  if (!correspondingCountries) return [userBuToCountryAndBuCodeMap[businessUnit][data]];
  return correspondingCountries.map((country) => userBuToCountryAndBuCodeMap[country][data]);
}

interface InternalUser {
  businessEmail: string;
  businessUnit: string;
  firstName: string;
  lastName: string;
  gadId: string;
  distinguishedName: string;
}

interface Permissions {
  readDocument: boolean;
  writeDocument: boolean;
}

@Injectable({
  providedIn: 'root',
})
export default class UserService {
  private readonly httpClient = inject(HttpClient);
  private readonly oauthService = inject(OAuthService);
  private readonly internalUser: WritableSignal<InternalUser | undefined> = signal(undefined);

  private readonly buCode: WritableSignal<string | undefined> = signal(undefined);

  async fetchInternalUser(gadId: string) {
    const id = gadId?.replace('GAD\\', '');
    const users = await lastValueFrom(
      this.httpClient.get<InternalUser[] | undefined>(
        `${environment.apiUrl}/userManagement/v2/internalUsers?gadId=${id}`,
      ),
    );
    return users?.at(0);
  }

  getInternalUser() {
    const internalUser = this.internalUser();
    return {
      ...internalUser,
      ...(internalUser?.businessUnit && {
        mappedBuCode: this.buCode() ?? userBuToCountryAndBuCodeMap[internalUser.businessUnit]?.buCode,
      }),
    };
  }

  async getBuCode(gadId: string) {
    const user = await this.fetchInternalUser(gadId);
    if (!user) return;
    return userBuToCountryAndBuCodeMap[user.businessUnit]?.buCode;
  }

  get buCodeSignal(): Signal<string | undefined> {
    return this.buCode;
  }

  setBuCode(buCode: string) {
    this.buCode.set(buCode);
  }

  async getDisplayName(gadId: string | undefined): Promise<string> {
    if (!gadId) return 'Unknown';
    try {
      const user = await this.fetchInternalUser(gadId);
      if (!user) return gadId;
      const { firstName, lastName, businessEmail } = user;
      const fullName = firstName && lastName && `${firstName} ${lastName}`;
      return fullName || businessEmail || gadId;
    } catch {
      return gadId;
    }
  }

  setInternalUser(internalUser?: InternalUser) {
    this.internalUser.set(internalUser);
  }

  getGadId(): string | undefined {
    if (!this.oauthService.hasValidAccessToken()) {
      return undefined;
    }
    const decodedJwt: jose.JWTPayload & { external_id?: string } = jose.decodeJwt(this.oauthService.getAccessToken());
    return decodedJwt.external_id;
  }

  potentialBuCodesSignal = computed(() => {
    const internalUser = this.internalUser();
    if (!internalUser) return [];

    const businessUnit = this.getBusinessUnitName(internalUser);
    return getDataFromBusinessUnit(businessUnit, 'buCode');
  });

  potentialCountryCodesSignal = computed(() => {
    const internalUser = this.internalUser();
    if (!internalUser) return [];

    const businessUnit = this.getBusinessUnitName(internalUser);
    return getDataFromBusinessUnit(businessUnit, 'countryCode');
  });

  private documentPermissionsQuery = injectQuery(() => {
    const buCode = this.buCodeSignal();
    const internalUser = this.internalUser();
    return {
      queryKey: ['documentPermissions', internalUser, buCode],
      queryFn: internalUser && buCode ? () => this.fetchDocumentPermissions(buCode) : skipToken,
      staleTime: Infinity,
    };
  });

  isLoadingPermissions = this.documentPermissionsQuery.isLoading;

  permissions = computed(() => {
    const permissions = this.documentPermissionsQuery.data();
    const isError = this.documentPermissionsQuery.isError();
    return {
      // if failed to load permissions, do not restrict
      readDocument: isError ? true : permissions?.readDocument,
      writeDocument: isError ? true : permissions?.writeDocument,
    };
  });

  private async fetchDocumentPermissions(buCode: string): Promise<Permissions> {
    const actions = await lastValueFrom(
      this.httpClient.post<string[]>(`${environment.apiUrl}/v3/permissions/actions/search`, {
        actions: ['jade_read_customer_vetting_document', 'jade_write_customer_vetting_document'],
        resource: `cve_${buCode.toLowerCase()}_*`,
      }),
    );
    return {
      readDocument: actions.includes('jade_read_customer_vetting_document'),
      writeDocument: actions.includes('jade_write_customer_vetting_document'),
    };
  }

  private getBusinessUnitName(user: InternalUser): string | undefined {
    if (!user?.businessUnit) return undefined;
    return user.businessUnit === '_External Accounts'
      ? parseDistinguishedName(user.distinguishedName)?.organizationalUnits[0]
      : user.businessUnit;
  }
}
